// Analítica de producto con PostHog (plan gratis).
//
// - Apagada si no hay VUE_APP_POSTHOG_KEY, y apagada en localhost aunque la
//   key esté en el .env: el desarrollo no gasta cupo ni mezcla datos. Para
//   probarla en local: localStorage.setItem('gme.analytics.local', '1').
// - Carga diferida: posthog-js (~105 KB gzip) se baja con import() después de
//   montar la app, así no suma a la carga inicial.
// - Pageviews por cambio de ruta (SPA). Sin autocapture de clicks: los eventos
//   que importan se mandan a mano con `track()`, para no gastar el cupo gratis.
// - Session replay: se prende, se apaga y se muestrea desde el proyecto en
//   PostHog (Settings → Session replay), sin tocar código. Los inputs salen
//   siempre enmascarados; cualquier bloque con la clase `ph-no-capture` no se
//   graba.
// - Personas: sin correo ni nombre. El id es un hash SHA-256 del correo
//   (estable, no reversible) y las propiedades son de la empresa y el rol.
// - Cuentas internas (GMEXP / Overview Effect, incluidas las de demo): por
//   ahora SÍ se registran, marcadas con `is_internal: true` para poder
//   filtrarlas en PostHog. Para dejar de registrarlas: EXCLUDE_INTERNAL = true.

// Si la variable no existe en Azure, el pipeline deja el texto `$(NOMBRE)`
// tal cual: eso cuenta como "sin configurar".
const envVar = (v) => (v && !String(v).startsWith('$(') ? String(v).trim() : '');
const KEY = envVar(process.env.VUE_APP_POSTHOG_KEY);
const HOST = envVar(process.env.VUE_APP_POSTHOG_HOST) || 'https://us.i.posthog.com';
const INTERNAL_EMAIL = /@([a-z0-9-]+\.)*(greenmyexperience\.com|overvieweffect\.io)$/i;
const EXCLUDE_INTERNAL = false;

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0', '[::1]'];
const isLocalHost = () => {
  const host = window.location.hostname;
  if (!LOCAL_HOSTS.includes(host) && !host.endsWith('.local')) return false;
  try { return localStorage.getItem('gme.analytics.local') !== '1'; } catch { return true; }
};

let client = null;
let loading = null;
const pending = [];

// Llamadas que llegan antes de que termine la carga diferida: se encolan.
const run = (fn) => {
  if (client) fn(client);
  else if (loading) pending.push(fn);
};

const sha256 = async (text) => {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

/** Arranca PostHog. Llamar una vez, después de `app.mount()`. */
export const initAnalytics = () => {
  if (!KEY || loading || isLocalHost()) return;
  // Sesión interna guardada: ni se carga (si no, el primer pageview sale
  // antes de que llegue el opt-out). Cerrar sesión recarga la página.
  try {
    const saved = JSON.parse(localStorage.getItem('gs-user') || 'null');
    if (EXCLUDE_INTERNAL && INTERNAL_EMAIL.test(String(saved?.email || '').trim())) return;
  } catch { /* sin storage o ilegible: se carga normal */ }
  loading = import(/* webpackChunkName: "posthog" */ 'posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(KEY, {
        api_host: HOST,
        person_profiles: 'identified_only',
        capture_pageview: 'history_change',
        capture_pageleave: true,
        autocapture: false,
        respect_dnt: true,
        session_recording: { maskAllInputs: true },
      });
      client = posthog;
      pending.splice(0).forEach((fn) => fn(client));
    })
    .catch(() => { loading = null; });
};

/** Identifica a quien inició sesión. `user` y `company` son los del login. */
export const identifyUser = (user, company) => run(async (ph) => {
  const email = String(user?.email || '').trim().toLowerCase();
  if (!email) return;
  const isInternal = INTERNAL_EMAIL.test(email);
  if (isInternal && EXCLUDE_INTERNAL) {
    ph.opt_out_capturing();
    return;
  }
  if (ph.has_opted_out_capturing()) ph.opt_in_capturing();
  ph.identify(await sha256(email), {
    role_id: user.rl ?? null,
    company_id: user.cid ?? company?.id ?? null,
    company_name: user.companyName || company?.trademark || company?.name || null,
    plan: company?.subscriptionType ?? null,
    tourism_sector: user.tourismSector ?? null,
    is_master: !!user.isMaster,
    is_network_owner: !!user.isNetworkOwner,
    is_internal: isInternal,
  });
});

/** Al cerrar sesión: lo que pase después ya no es de esa persona. */
export const resetAnalytics = () => run((ph) => ph.reset());

/** Evento de producto: `track('pdf_downloaded', { report: 'monitor' })`. */
export const track = (event, props = {}) => run((ph) => ph.capture(event, props));
